import React, { useState, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import './table.css';

export interface ColumnConfig {
  id: string;
  title: string;
  render: (rowData: any) => React.ReactNode;
  width?: string;
  className?: string;
}

export interface ExpandedRowConfig {
  id: string;
  content: (rowData: any) => React.ReactNode;
}

export interface SecondaryRowConfig {
  id: string;
  content: (rowData: any) => React.ReactNode;
  condition?: (rowData: any) => boolean;
}

interface TableProps {
  data: any[];
  columns: ColumnConfig[];
  expandedRows?: ExpandedRowConfig[];
  secondaryRows?: SecondaryRowConfig[];
  keyField: string;
  noDataMessage?: string;
  className?: string;
  rowClassName?: string;
}

export const LabeledCell: React.FC<{
  title: string;
  value: React.ReactNode;
  colSpan?: number;
  className?: string;
}> = ({ title, value, colSpan, className = 'table-cell' }) => (
  <td className={className} colSpan={colSpan}>
    <div className="table-cell-title">{title}</div>
    <div className="table-cell-value">{value}</div>
  </td>
);

export const LabeledSecondaryCell: React.FC<{
  title: string;
  value: React.ReactNode;
  colSpan?: number;
}> = ({ title, value, colSpan }) => (
  <td className="table-secondary-cell" colSpan={colSpan}>
    <div className="table-secondary-title">{title}</div>
    <div className="table-secondary-value">{value}</div>
  </td>
);

const Table: React.FC<TableProps> = ({
  data,
  columns,
  expandedRows = [],
  secondaryRows = [],
  keyField,
  noDataMessage = 'Нет данных',
  className = 'table-component',
  rowClassName = 'table-component-row',
}) => {
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const rowRefs = useRef<Record<string, HTMLElement | null>>({});

  const toggleRow = (id: string) => {
    setOpenRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  if (data.length === 0) {
    return <div className="no-data-message">{noDataMessage}</div>;
  }

  return (
    <>
      {data.map((row) => {
        const rowKey = row[keyField];
        const isOpen = !!openRows[rowKey];

        return (
          <table className={className} key={rowKey}>
            <tbody>
              <tr className={rowClassName}>
                {columns.map((column) => {
                  if (column.id === 'actions') {
                    return (
                      <td key={column.id} className="table-cell-action" onClick={() => toggleRow(rowKey)}>
                        <span>{column.title}</span>
                        <svg
                          className={isOpen ? 'icon-active' : ''}
                          width="11"
                          height="7"
                          viewBox="0 0 11 7"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ verticalAlign: 'middle', marginLeft: '5px' }}
                        >
                          <path
                            d="M1 1.5L5.33333 5.5L10 1.5"
                            stroke="#A2A2A2"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </td>
                    );
                  }
                  return (
                    <td key={column.id} className={column.className || 'table-cell'}>
                      {column.render(row)}
                    </td>
                  );
                })}
              </tr>

              {/* Вторичные строки (видимые всегда) */}
              {secondaryRows.map((secondaryRow) => {
                // Если есть условие отображения и оно не выполняется, не показываем строку
                if (secondaryRow.condition && !secondaryRow.condition(row)) {
                  return null;
                }

                return (
                  <tr key={`${rowKey}-${secondaryRow.id}`} className={rowClassName}>
                    {secondaryRow.content(row)}
                  </tr>
                );
              })}

              {/* Разделительная линия между видимой и раскрывающейся частью */}
              {isOpen && expandedRows.length > 0 && (
                <tr className="divider-row">
                  <td colSpan={columns.length}></td>
                </tr>
              )}

              {/* Расширяемые строки (видимые при раскрытии) */}
              {expandedRows.map((expandedRow, idx) => (
                <CSSTransition
                  key={`${rowKey}-${expandedRow.id}`}
                  in={isOpen}
                  timeout={111}
                  classNames={{
                    enter: 'slide-enter',
                    enterActive: 'slide-enter-active',
                    exit: 'slide-exit',
                    exitActive: 'slide-exit-active',
                  }}
                  unmountOnExit
                  nodeRef={{ current: rowRefs.current[`${rowKey}-${expandedRow.id}`] }}
                >
                  <tr ref={(el) => (rowRefs.current[`${rowKey}-${expandedRow.id}`] = el)} className={rowClassName}>
                    {expandedRow.content(row)}
                  </tr>
                </CSSTransition>
              ))}
            </tbody>
          </table>
        );
      })}
    </>
  );
};

export default Table;
